/**
 * The keys to the institute, drawn beside the roles heading.
 *
 * Decorative and `aria-hidden` — it says nothing the chips beside it do not.
 * Its own subject again: the profile draws the campus, the roster the climb,
 * the register the shape of the roll, the admission form the card it issues,
 * the counter its till, and this draws what this screen hands out — a shield
 * with a keyhole, and the keys that open it.
 *
 * Built from `fill-*` utilities rather than gradient stops so it re-tints with
 * the theme (CLAUDE.md §7), and animated only with keyframes the product
 * already ships, every one of which is held still under
 * `prefers-reduced-motion`.
 */
export function AccessGraphic() {
  return (
    <svg viewBox="0 0 200 140" className="h-auto w-full" role="presentation" aria-hidden>
      {/* the light behind it */}
      <circle cx="96" cy="68" r="52" className="fill-blue-200/60 dark:fill-blue-500/10" />
      <circle
        cx="96"
        cy="68"
        r="34"
        className="fill-sky-200/55 animate-glow dark:fill-sky-500/10"
      />

      {/* The shield. Its face is a lighter plate so the keyhole reads. */}
      <path
        d="M96 18 L142 34 L142 74 C142 100 120 118 96 126 C72 118 50 100 50 74 L50 34 Z"
        className="fill-indigo-500 dark:fill-indigo-400"
      />
      <path
        d="M96 27 L133 40 L133 73 C133 94 116 109 96 116 C76 109 59 94 59 73 L59 40 Z"
        className="fill-blue-600 dark:fill-blue-500"
      />

      {/* the keyhole at its centre */}
      <circle cx="96" cy="64" r="10" className="fill-white/90 dark:fill-slate-100/85" />
      <path d="M91 70 L101 70 L98.5 88 L93.5 88 Z" className="fill-white/90 dark:fill-slate-100/85" />
      <circle cx="96" cy="64" r="4.5" className="fill-blue-700 dark:fill-blue-600" />

      {/* A pass of light across the plate, so the shield is never quite flat. */}
      <path
        d="M96 27 L133 40 L133 73 C133 94 116 109 96 116 C76 109 59 94 59 73 L59 40 Z"
        className="fill-white/15 animate-glow"
      />

      {/* Two keys on a ring, swinging beside it. */}
      <g className="animate-float-slow">
        <circle cx="30" cy="52" r="7" className="fill-none stroke-amber" strokeWidth="3.5" />
        <rect x="34" y="50" width="22" height="4" rx="2" className="fill-amber" />
        <rect x="48" y="54" width="4" height="6" rx="2" className="fill-amber" />
        <rect x="54" y="54" width="4" height="5" rx="2" className="fill-amber" />
      </g>
      <g className="animate-float-slow delay-500">
        <circle cx="170" cy="94" r="6" className="fill-none stroke-sky-400" strokeWidth="3" />
        <rect x="152" y="92" width="16" height="3.5" rx="1.75" className="fill-sky-400" />
        <rect x="154" y="95" width="3.5" height="5" rx="1.75" className="fill-sky-400" />
      </g>

      {/* the ground it stands on */}
      <rect x="56" y="128" width="80" height="5" rx="2.5" className="fill-blue-300/60 dark:fill-blue-400/25" />

      {/* sparks around the shield */}
      <circle cx="158" cy="42" r="3" className="fill-amber animate-sparkle" />
      <circle cx="36" cy="98" r="2.5" className="fill-sky-400 animate-sparkle delay-500" />
      <circle cx="150" cy="118" r="2" className="fill-blue-400 animate-sparkle delay-200" />
      <circle cx="70" cy="20" r="2.5" className="fill-indigo-400 animate-sparkle delay-700" />
    </svg>
  );
}
